import { useState } from 'react';
import { Activity, Plug, Workflow } from 'lucide-react';
import { cn } from '@/lib/utils';
import Monitoring from './Monitoring/Monitoring';
import Workflows from './Workflows/Workflows';
import Connections from './Connections';

type TabType = 'monitoring' | 'workflows' | 'connections';

const AgentsDashboard = () => {
    const [activeTab, setActiveTab] = useState<TabType>('monitoring');

    const tabs = [
        {
            id: 'monitoring' as TabType,
            label: 'Monitoring',
            icon: Activity,
            description: 'Monitor workflow runs and performance',
        },
        {
            id: 'workflows' as TabType,
            label: 'Workflows',
            icon: Workflow,
            description: 'Create and manage agentic workflows',
        },
        {
            id: 'connections' as TabType,
            label: 'Connections',
            icon: Plug,
            description: 'Configure integrations for your workflows',
        },
    ];

    const renderActiveTab = () => {
        switch (activeTab) {
            case 'monitoring':
                return <Monitoring />;
            case 'workflows':
                return <Workflows />;
            case 'connections':
                return <Connections />;
            default:
                return <Monitoring />;
        }
    };

    return (
        <div className="w-full">
            <div
                className="mb-4 px-4 py-2 rounded text-center font-semibold bg-background border border-secondary text-primary"
            >
                <span role="img" aria-label="Demo">🛈</span> This Agents Section is running in <strong>demo mode</strong>. Data is sampled for demonstration purposes and actions will not have any real effect.
            </div>
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center mb-4">
                    <Workflow className="mr-3 text-primary" size={32} />
                    <div>
                        <h1 className="text-3xl font-bold text-foreground">
                            Agents
                        </h1>
                        <p className="text-lg text-muted-foreground">
                            Manage agentic workflows and monitor their performance
                        </p>
                    </div>
                </div>

                {/* Overview Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;

                        return (
                            <div
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={cn(
                                    "p-4 rounded-lg cursor-pointer transition-all duration-200 border",
                                    isActive 
                                        ? "bg-primary/10 border-primary ring-2 ring-primary" 
                                        : "bg-background border-border hover:shadow-md hover:bg-muted/50"
                                )}
                            >
                                <div className="flex items-center mb-2">
                                    <Icon
                                        size={24}
                                        className={cn(
                                            isActive ? "text-primary" : "text-muted-foreground"
                                        )}
                                    />
                                    <h3 className={cn(
                                        "ml-3 font-semibold",
                                        isActive ? "text-primary" : "text-foreground"
                                    )}>
                                        {tab.label}
                                    </h3>
                                </div>
                                <p className={cn(
                                    "text-sm",
                                    isActive ? "text-foreground" : "text-muted-foreground"
                                )}>
                                    {tab.description}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="mb-6">
                <div className="border-b border-border">
                    <nav className="-mb-px flex space-x-8">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={cn(
                                        "py-2 px-1 border-b-2 font-medium text-sm flex items-center transition-colors",
                                        isActive 
                                            ? "border-primary text-primary" 
                                            : "border-transparent text-muted-foreground hover:text-foreground"
                                    )}
                                >
                                    <Icon size={16} className="mr-2" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Tab Content */}
            <div className="mt-6">
                {renderActiveTab()}
            </div>
        </div>
    );
};

export default AgentsDashboard;
